import {
  Slot,
  component$,
  useStore,
  useContextProvider,
  createContextId,
  $,
} from "@builder.io/qwik";
import type { DataActionType, DataContextType } from "../types/DataContextType";
import {
  calculateCandleWidthDate,
  dateArrayNormalizer,
} from "../utils/helperFunctions";
import * as d3 from "d3";
import _ from "lodash-es";
import { calculatePriceDaily } from "~/helper";
// import { extractChartValues } from "~/components/bar-chart/utils/helperFunctions";

export const extractChartValues = (data: any, key: string) => {
  const datakeys = (data?.keys || []).filter((d) => d).map((d) => d.key);
  const series = (data?.bars || [])
    .filter((d) => !d.hide)
    .filter((d) => d.has_value)
    .map((d) => ({
      attr: d.key,
      name: d.name,
      color: d.color,
      hide_by_default: d.hide_by_default,
    }));

  const arr = data[key];
  const xs = arr.map((d) => d.datestr);
  const datas = series.map((s) => ({
    data: arr.map((d) => d.ret[datakeys.indexOf(s.attr)]),
    name: s.name,
    color: s.color,
    hide_by_default: s.hide_by_default,
  }));
  // .filter((d) => {
  //   return !_.every(d.data, (d) => !d);
  // });

  // Sort xs and datas by date ascending before assigning to rawPriceSignal
  const sorted = xs
    .map((date, i) => ({
      date,
      is_eoy: arr[i].is_eoy,
      prices: datas.map((dd) => dd.data[i]),
    }))
    .filter((d) => !Number.isNaN(new Date(d.date).getTime()))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return { data: sorted, series };
};

const MAX_CANDLE = 20;
const MIN_CANDLE = 19;

const TIMEFRAMEILY_MAPPING = {
  quarter: "Quarterly",
  annual: "Annual",
};

const _initData: DataContextType = {
  dateToIdxMapping: {},
  idxToDateMapping: {},
  ticker: "",
  rawData: [],
  maxShownVolume: 0,
  timeframe: "quarter",
  timeframily: TIMEFRAMEILY_MAPPING["quarter"],
  initData: [],
  shownData: [],
  shownRange: {
    start: 0,
    end: 0,
  },
  minMaxInitDate: {
    min: 0,
    max: 0,
  },
  minMaxInitPrice: {
    min: 0,
    max: 0,
  },
  minMaxShownDate: {
    min: 0,
    max: 0,
  },
  minMaxShownPrice: {
    min: 0,
    max: 0,
  },
  minMaxShownMACD: {
    min: 0,
    max: 0,
  },
  zoomFactor: 1,
  incrementZoomFactor: 1.08,
  decrementZoomFactor: 0.9,
  candleWidthDate: 0,
  candleLockerWidthDate: 0,
};

export const DataContext = createContextId<DataContextType>("DataContext");
export const DataDispatchContext = createContextId<
  (action: DataActionType) => void
>("DataDispatchContext");

function calculatePriceByTimeframe(rawdata: any, timeframe: string) {
  return calculatePriceDaily(rawdata);
}

function calcCandleWidth(
  minMaxShownDate: { min: number; max: number },
  candleWidthDate: number,
  shownRangeStart,
  shownRangeEnd,
  configCanvasWidth,
) {
  const xScaleFunction = d3
    .scaleTime()
    .domain([shownRangeStart, shownRangeEnd])
    .range([0, configCanvasWidth ?? 0]);
  let candleWidth =
    xScaleFunction(minMaxShownDate.min + candleWidthDate) -
    xScaleFunction(minMaxShownDate.min);
  candleWidth = Number.isNaN(candleWidth) ? 0 : candleWidth;
  return candleWidth;
}

const timeframeToTimeframeDataKeyMapping = {
  quarter: "quarterly",
  annual: "annual",
};
function dataReducer(state: DataContextType, action: DataActionType) {
  switch (action.type) {
    case "changeInitData": {
      if (!action.initData) return;
      state.timeframe = action.timeframe;
      if (
        !action.initData[timeframeToTimeframeDataKeyMapping[state.timeframe]]
          .length
      )
        return;
      if (action.canvasWidth) {
        state.canvasWidth = action.canvasWidth;
      }
      const { data, series } = extractChartValues(
        action.initData,
        timeframeToTimeframeDataKeyMapping[state.timeframe],
      );
      if (series) {
        state.series = series;
      }

      state.timeframily = TIMEFRAMEILY_MAPPING[action.timeframe];
      state.rawData = _.cloneDeep(action.initData);
      const initData = data;
      const ticker = action.ticker;

      const currentData = state.initData;
      const currentTicker = state.ticker;

      if (currentTicker !== ticker) {
        state.ticker = ticker;
      } else {
        // this fixs and issue when the data changes, the next zoom is based on the previous chart
        if (currentData.length && initData.length) {
          state.zoomFactor =
            (state.zoomFactor * initData.length) / currentData.length;

          if (initData.length > currentData.length) {
            state.shownRange = {
              start:
                state.shownRange.start + (initData.length - currentData.length),
              end:
                state.shownRange.end + (initData.length - currentData.length),
            };
          } else {
            state.shownRange = {
              start: 0,
              end: 1,
            };
          }
        }
      }

      const dateToIdxMapping = {};
      const idxToDateMapping = {};

      // oldest date is at idx 0 because lower numbers are drawn on the beginning of the chart.
      const newInitData = _.cloneDeep(initData)
        .sort((a, b) => b.date - a.date)
        .map((d, idx) => {
          dateToIdxMapping[d.date] = idx;
          idxToDateMapping[idx] = d.is_eoy ? `${d.date} ⏰` : `${d.date}`;
          d.date = idx;
          return d;
        });

      state.dateToIdxMapping = dateToIdxMapping;
      state.idxToDateMapping = idxToDateMapping;
      const initDates = newInitData.map((x) => x.date);
      const normalizeInitDates = dateArrayNormalizer(initDates) as number[];
      const highPrices: number[] = newInitData.flatMap(
        (x) => x.prices as number,
      );
      const lowPrices: number[] = newInitData.flatMap(
        (x) => x.prices as number,
      );
      const newMinMaxInitPrice = d3.extent(
        [...highPrices, ...lowPrices].filter((d) => typeof d === "number"),
      );
      const lastIndex = normalizeInitDates.length - 1;

      // TODO: what is this
      // {candleWidth: 60480000, candleLockerWidth: 86400000}
      // this change should happen when we change the charttpe
      const [candleWidth, candleLockerWidth] =
        calculateCandleWidthDate(normalizeInitDates);
      state.candleWidthDate = candleWidth;
      state.candleLockerWidthDate = candleLockerWidth;

      state.initData = newInitData;
      state.shownData = newInitData;
      state.minMaxInitDate = {
        min: (normalizeInitDates[0] - candleWidth / 2) as number,
        max: (normalizeInitDates[lastIndex] + candleWidth / 2) as number,
      };
      state.minMaxShownDate = {
        min: (normalizeInitDates[0] - candleWidth / 2) as number,
        max: (normalizeInitDates[lastIndex] + candleWidth / 2) as number,
      };
      state.minMaxInitPrice = {
        min: newMinMaxInitPrice[0] as number,
        max: newMinMaxInitPrice[1] as number,
      };

      // state.zoomFactor = state.zoomFactor;
      // state.incrementZoomFactor = state.incrementZoomFactor;
      // state.decrementZoomFactor = state.decrementZoomFactor;
      break;
    }
    case "changeShownRange": {
      const newShownRange = action.shownRange;

      newShownRange.start =
        newShownRange.start < state.minMaxInitDate.min
          ? state.minMaxInitDate.min
          : newShownRange.start;
      newShownRange.end =
        newShownRange.end > state.minMaxInitDate.max
          ? state.minMaxInitDate.max
          : newShownRange.end;
      const currentCandleCount = state.shownRange.end - state.shownRange.start;

      if (action.zoomValue == "up") {
        if (currentCandleCount <= MIN_CANDLE) {
          newShownRange.end = Math.min(
            newShownRange.start + MIN_CANDLE,
            state.minMaxInitDate.max,
          );
        }
        // newShownRange.start = Math.min(newShownRange.end, newShownRange.start + MAX_CANDLE)
        // newShownRange.start = Math.max(newShownRange.start, newShownRange.end - MAX_CANDLE)
      } else if (action.zoomValue == "down") {
        if (currentCandleCount >= MAX_CANDLE) {
          newShownRange.end = Math.min(
            newShownRange.start + MAX_CANDLE,
            state.minMaxInitDate.max,
          );
        }
      }
      const zoomFactorCalc: number =
        (state.minMaxInitDate.max - state.minMaxInitDate.min) /
        (newShownRange.end - newShownRange.start);
      const newShownData = state.initData.filter(
        (x) =>
          x.date < newShownRange.end + state.candleWidthDate / 2 &&
          x.date > newShownRange.start - state.candleWidthDate / 2,
      );
      const allPrices: number[] = newShownData
        .flatMap((x) => x.prices as number)
        .filter((x) => x !== undefined && x !== null && x !== "")
        .filter((d) => typeof d === "number");
      const newMinMaxShownPrice = d3.extent(allPrices);

      newMinMaxShownPrice[0] = newMinMaxShownPrice[0]
        ? newMinMaxShownPrice[0]
        : 0;
      newMinMaxShownPrice[1] = newMinMaxShownPrice[0]
        ? newMinMaxShownPrice[1]
        : 0;
      state.shownData = newShownData;
      state.minMaxShownDate = {
        min: newShownRange.start,
        max: newShownRange.end,
      };
      state.minMaxShownPrice = {
        min: newMinMaxShownPrice[0] as number,
        max: newMinMaxShownPrice[1] as number,
      };
      state.shownRange = newShownRange;
      state.zoomFactor = zoomFactorCalc;
      state.candleWidth = calcCandleWidth(
        state.minMaxShownDate,
        state.candleWidthDate,
        state.shownRange.start,
        state.shownRange.end,
        state.canvasWidth,
      );
      break;
    }
    default: {
      break;
    }
  }
}

export const DataProvider = component$(() => {
  const data = useStore({ ..._initData });
  const dispatch = $((action: DataActionType) => {
    dataReducer(data, action);
  });
  useContextProvider(DataContext, data);
  useContextProvider(DataDispatchContext, dispatch);
  return <Slot />;
});

export const getData = DataContext;
export const getDataDispatch = DataDispatchContext;
